import { formatDate } from 'skyeye-fe-common-util';
import { EMPTY_STRING, YEARS_FROM_NOW, RESULT_PER_PAGE, DURATION, DURATION_IN_DAY, ARTICLE_PUBLISHER, SwaggerDocEnvironmentMap } from '@constants/common';
import { newsConst } from 'skyeye-common-const';
import { CalendarInterval } from '@interfaces/common.interface';
import qs from 'query-string';
import papa from 'papaparse';
import { isEmpty, keyBy } from 'lodash';
import { IOidcConfiguration } from '@react/models/oidcConfiguration';
import * as XLSX from 'xlsx';
import { red } from '@material-ui/core/colors';
import { format } from 'date-fns';

const guidPattern =
  '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89ab][0-9A-Fa-f]{3}-[0-9A-Fa-f]{12}';

export const isGUID = (input: string = EMPTY_STRING) => {
  const guidRegex = new RegExp(`^${guidPattern}$`, 'i');
  const match = input.match(guidRegex);

  return !!match;
};

export const containCaseInsensitive = (fullString: string, text: string) => {
  const regex = new RegExp(text, 'i');

  return regex.test(fullString);
};

export const isSearchMultipleArticles = (input: string = EMPTY_STRING) => {
  const searchMultiScoutRegex = new RegExp(
    `(${guidPattern} OR )*${guidPattern}`,
    'gi'
  );
  const match = input.match(searchMultiScoutRegex);

  return !!match;
};

export const isSearchWithDate = (input: string = EMPTY_STRING) => {
  const searchWithDateRegex = /[dD]ate: (?<start>[0-9]{4}-[0-9]{2}-[0-9]{2}) TO (?<end>[0-9]{4}-[0-9]{2}-[0-9]{2})/gi;
  // const match = input.match(searchWithDateRegex);
  const match = searchWithDateRegex.exec(input);
  return match;
};

export const isValidFirstName = (firstName: string) => {
  const firstNameRegExp = /^[A-Za-z]([A-Za-z\s]*)[A-Za-z]$/;
  return firstNameRegExp.test(firstName);
};

export const isValidLastName = (lastName: string) => {
  const lastNameRegExp = /^[A-Za-z]{2,}$/;
  return lastNameRegExp.test(lastName);
};

export const isValidMaxUsers = (maxUsers: number) => {
 return maxUsers > 1;
}

export const isValidEmail = (email: string = '') => {
  const emailRegex = new RegExp(
    '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
  );
  const match = email ? email.match(emailRegex) : false;

  return !!match;
};

export const isValidContact = (contact: string) => {
  if (!contact || !contact.includes('|')) {
    return true;
  }
  const wordRegex = new RegExp('^[0-9]{8,}$', 'g');
  const contactNumber = contact.split('|')[1];
  const match = contactNumber.match(wordRegex);

  return !!match;
};

export const getFormattedDate = (dateString: string = EMPTY_STRING) => {
  if (dateString) {
    return formatDate(new Date(dateString), 'DD/MM/YYYY');
  }
  return dateString;
};

export const getSearchQueryString = (queryObject: string) => {
  const queryString = encodeURIComponent(JSON.stringify(queryObject));
  return btoa(queryString);
};

export const isEqualCaseInsensitive = (
  a: string = EMPTY_STRING,
  b: string = EMPTY_STRING
) => {
  if (isEmpty(a) || isEmpty(b)) {
    return false;
  }
  return a.toLowerCase() === b.toLowerCase();
};

export const getInitialInterval = (
  start: string = EMPTY_STRING,
  end: string = EMPTY_STRING
): CalendarInterval => {
  if (start && end) {
    return {
      start: new Date(start),
      end: new Date(end),
    };
  }

  return {
    start: new Date(),
    end: new Date(),
  };
};

export const stillActive = (endDateString: string) => {
  const currentDate = new Date();
  const endDate = new Date(endDateString);

  return endDate.getTime() > currentDate.getTime();
};

export const getDefaultInterval = (
  start: string = EMPTY_STRING
): CalendarInterval => {
  let startDate = new Date();
  if (start && new Date(start) < startDate) {
    startDate = new Date(start);
  }
  const endDate = new Date();
  endDate.setFullYear(endDate.getFullYear() + YEARS_FROM_NOW);
  return {
    start: startDate,
    end: endDate,
  };
};

export const changePageQuery = (history, query) => {
  history.push({
    pathname: history.location.pathname,
    search: qs.stringify(query),
  });
};

export const isNotValidPage = (total: number, page: number) => {
  const maxPage = total ? Math.ceil(total / RESULT_PER_PAGE) : 1;
  return page > maxPage || page < 1;
};

export const getValidPage = (total: number, currentPage: number) => {
  if (!isNotValidPage(total, currentPage)) {
    return currentPage;
  }
  if (currentPage < 1) {
    return getValidPage(total, currentPage + 1);
  }
  return getValidPage(total, currentPage - 1);
};

export const decodeOidcConfiguration = (
  oidcConfigurationString: string
): IOidcConfiguration => {
  if (isEmpty(oidcConfigurationString)) {
    return {
      authority: '',
      clientId: '',
      clientSecret: '',
      redirectUri: '',
      scope: '',
      silentRedirectUri: '',
    };
  }

  const configObject = Buffer.from(oidcConfigurationString, 'base64').toString(
    'ascii'
  );
  return JSON.parse(decodeURIComponent(configObject));
};

export const generateDurationFromDays = (period: number) => {
  let durationType = DURATION.DAY;
  let durationNumber = 0;
  if (period === 0) {
    return { durationType, durationNumber };
  }

  if (period % DURATION_IN_DAY.YEAR === 0) {
    durationType = DURATION.YEAR;
    durationNumber = period / DURATION_IN_DAY.YEAR;
  } else if (period % DURATION_IN_DAY.MONTH === 0) {
    durationType = DURATION.MONTH;
    durationNumber = period / DURATION_IN_DAY.MONTH;
  } else if (period % DURATION_IN_DAY.WEEK === 0) {
    durationType = DURATION.WEEK;
    durationNumber = period / DURATION_IN_DAY.WEEK;
  } else if (period % DURATION_IN_DAY.DAY === 0) {
    durationType = DURATION.DAY;
    durationNumber = period / DURATION_IN_DAY.DAY;
  }

  return { durationType, durationNumber };
};

export const convertJsonAndDownloadCsv = (json, fileName: string) => {
  const csvString = papa.unparse(json);
  const filename = fileName;
  const blob = new Blob([csvString], { type: 'text/plain' });
  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveBlob(blob, filename);
  } else {
    const a = window.document.createElement('a');

    a.href = window.URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

export const toSentenceCase = (str: string) => {
  if (!str || str.length < 2) {
    return str;
  }
  const newString = str.toLowerCase();
  return newString.slice(0, 1).toUpperCase() + newString.slice(1);
};

export const removeHTMLTagFromContent = (contentBody: string) => {
  const parser = new DOMParser();
  const newContent = parser.parseFromString(contentBody, 'text/html');
  return newContent.title || newContent.body.outerHTML;
};

export const addParagraphTagToContent = (contentBody: string) => {
  const newContent = `<p>${contentBody?.replace(/\n/g, '</p> <p>')}</p>`;
  return newContent;
};

// removes HTML from string
export const stripHtmlTags = (text: string) => {
  const tempDiv: HTMLDivElement = document.createElement('div');
  tempDiv.innerHTML = text;
  return tempDiv.textContent || tempDiv.innerText || EMPTY_STRING;
};

export const getPublisherLabel = (
  publisherCode: string,
  providerTitle: string
): string => {
  /**
   * Show codes for the following publishers - EMIS, SGX and HKEX.
   */
  if (publisherCode === ARTICLE_PUBLISHER.NAVIGA && providerTitle) {
    return providerTitle;
  }
  if (ARTICLE_PUBLISHER[publisherCode]) {
    return ARTICLE_PUBLISHER[publisherCode];
  }

  const publisherMap = keyBy(Object.values(newsConst), 'code');
  if (publisherCode && publisherMap[publisherCode]) {
    return publisherMap[publisherCode].label.replace(/ *\([^)]*\) */g, '');
  }
  return EMPTY_STRING;
};

export const getSwaggerDocUrl = () => {
  const env = window.location.hostname.split('.')[0];
  const docEnv = SwaggerDocEnvironmentMap[env];
  return `https://${docEnv ||
    SwaggerDocEnvironmentMap.portal}swagger.scout.asia/api-doc/`;
};

export const assertNever = (x: never): never => {
  throw new Error(`Unexpected object: ${x}`);
};

export const formatTimeStamp = (timestamp: number, timeLocal?: boolean) => {
  if (!timestamp) {
    return '';
  }
  try {
    const date = new Date(Number(timestamp));
    if (timeLocal) {
      return format(date, 'dd MMM yyyy hh:mm a');
    }
    const utcDate = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
    );
    const formattedDate = format(utcDate, 'dd MMM yyyy HH:mm a');

    return `${formattedDate} (UTC+0)`;
  } catch (error) {
    return '';
  }
};

export const formatUnixTimeStamp = (
  timestamp: number,
  exportExcel?: boolean,
) => {
  if (!timestamp) {
    return '';
  }
  try {
    const date = new Date(Number(timestamp) * 1000);
    const utcDate = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
    );
    const formattedDate = format(utcDate, 'dd MMM yyyy HH:mm a');
    return exportExcel ? formattedDate : `${formattedDate} (UTC+0)`;
  } catch (error) {
    return '';
  }
};

export const exportToExcel = (
  title: Record<string, string>,
  data: any[],
  fileName: string = 'ExportedData.xlsx'
) => {
  const formattedData = data.map(item =>
    Object.keys(title).reduce((acc, key) => {
      acc[title[key]] = item[key];
      return acc;
    }, {})
  );
  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  worksheet['!cols'] = Object.keys(title).map(() => ({ width: 20 }));
  Object.keys(worksheet).forEach(key => {
    if (key.startsWith('!')) return;
    worksheet[key].s = {
      backdrop: red
    };
  });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'List');
  XLSX.writeFile(workbook, fileName);
};
